import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["rows", "template"]

  static values = {
    options: Array
  }

  connect() {
    this.options = Array.isArray(this.optionsValue) ? this.optionsValue : []
    this.initializeRows()
  }

  addRow() {
    if (!this.hasRowsTarget || !this.hasTemplateTarget) {
      return
    }

    this.rowsTarget.insertAdjacentHTML("beforeend", this.templateTarget.innerHTML)
    const newRow = this.rowsTarget.lastElementChild
    if (newRow) {
      this.initializeRow(newRow)
    }
  }

  removeRow(event) {
    const row = event.currentTarget.closest("[data-explanation-row]")
    if (!row) {
      return
    }

    row.remove()
  }

  onKeyInput(event) {
    const input = event.currentTarget
    const row = input.closest("[data-explanation-row]")
    if (!row) {
      return
    }

    if ((input.value || "").trim().length > 0) {
      this.setRowSelectedKey(row, "")
    }

    this.renderDropdown(row, input.value)
  }

  onKeyFocus(event) {
    const input = event.currentTarget
    const row = input.closest("[data-explanation-row]")
    if (!row) {
      return
    }

    this.renderDropdown(row, input.value)
  }

  onKeyBlur(event) {
    const input = event.currentTarget
    const row = input.closest("[data-explanation-row]")
    if (!row) {
      return
    }

    setTimeout(() => this.hideDropdown(row), 150)
  }

  selectOption(event) {
    event.preventDefault()

    const button = event.currentTarget
    const row = button.closest("[data-explanation-row]")
    if (!row) {
      return
    }

    const key = String(button.dataset.key || "")
    this.setRowSelectedKey(row, key)
    this.hideDropdown(row)
  }

  toggleChip(event) {
    event.preventDefault()

    const removeClicked = event.target?.closest?.("[data-remove='true']")
    if (!removeClicked) {
      return
    }

    const row = event.currentTarget.closest("[data-explanation-row]")
    if (!row) {
      return
    }

    this.setRowSelectedKey(row, "")
    const input = row.querySelector("[data-role='key-input']")
    if (input) {
      input.focus()
    }
  }

  initializeRows() {
    if (!this.hasRowsTarget) {
      return
    }

    this.rowsTarget.querySelectorAll("[data-explanation-row]").forEach((row) => this.initializeRow(row))
  }

  initializeRow(row) {
    this.initializeExplanationEditor(row)
    const selectedKey = this.selectedKeyFromRow(row)
    this.setRowSelectedKey(row, selectedKey)
  }

  initializeExplanationEditor(row) {
    const hiddenInput = row.querySelector("[data-role='explanation-hidden']")
    const editor = row.querySelector("[data-role='explanation-editor']")
    if (!hiddenInput || !editor) {
      return
    }

    if (!hiddenInput.id) {
      hiddenInput.id = `template_explanation_${Date.now()}_${Math.floor(Math.random() * 100000)}`
    }
    editor.setAttribute("input", hiddenInput.id)
  }

  renderDropdown(row, query) {
    const dropdown = row.querySelector("[data-role='key-dropdown']")
    if (!dropdown) {
      return
    }

    const term = String(query || "").toLowerCase().trim()
    const matches = this.options.filter((option) => {
      const key = String(option.key || "").toLowerCase()
      const title = String(option.title || "").toLowerCase()
      const type = String(option.type || "").toLowerCase()
      const detail = String(option.detail || "").toLowerCase()
      if (!term) {
        return true
      }
      return key.includes(term) || title.includes(term) || type.includes(term) || detail.includes(term)
    })

    if (!matches.length) {
      dropdown.classList.add("hidden")
      dropdown.innerHTML = ""
      return
    }

    dropdown.innerHTML = matches
      .map((option) => {
        const key = this.escapeHtml(String(option.key || ""))
        const title = this.escapeHtml(String(option.title || option.key || ""))
        const type = this.escapeHtml(String(option.type || "Key"))
        const detail = this.escapeHtml(String(option.detail || ""))
        return `<button type="button" class="block w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-white/5" data-action="mousedown->explanation-rows#selectOption" data-key="${key}"><div class="flex items-center justify-between gap-2"><span class="truncate text-sm font-medium text-gray-800 dark:text-gray-100">${title}</span><span class="shrink-0 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">${type}</span></div><div class="mt-1 flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400"><span class="truncate">${detail}</span><span class="shrink-0">(${key})</span></div></button>`
      })
      .join("")

    dropdown.classList.remove("hidden")
  }

  hideDropdown(row) {
    const dropdown = row.querySelector("[data-role='key-dropdown']")
    if (!dropdown) {
      return
    }

    dropdown.classList.add("hidden")
  }

  setRowSelectedKey(row, key) {
    const selectedKey = String(key || "").trim()
    const hiddenInput = row.querySelector("[data-role='key-hidden']")
    const visibleInput = row.querySelector("[data-role='key-input']")
    const chip = row.querySelector("[data-role='key-chip']")
    const chipText = row.querySelector("[data-role='chip-text']")

    if (hiddenInput) {
      hiddenInput.value = selectedKey
    }

    if (!selectedKey) {
      if (visibleInput) {
        visibleInput.value = ""
        visibleInput.classList.remove("hidden")
      }
      if (chip) {
        chip.classList.add("hidden")
      }
      return
    }

    if (visibleInput) {
      visibleInput.value = ""
      visibleInput.classList.remove("hidden")
    }

    if (chip && chipText) {
      chipText.textContent = this.optionLabel(selectedKey)
      chip.classList.remove("hidden")
    }
  }

  selectedKeyFromRow(row) {
    const hiddenInput = row.querySelector("[data-role='key-hidden']")
    return String(hiddenInput?.value || "").trim()
  }

  optionLabel(key) {
    const selectedKey = String(key || "")
    const match = this.options.find((option) => String(option.key || "") === selectedKey)
    const title = String(match?.title || "").trim()
    const type = String(match?.type || "").trim()
    if (title.length > 0 && type.length > 0) {
      return `${type}: ${title} (${selectedKey})`
    }
    return title.length > 0 ? `${title} (${selectedKey})` : selectedKey
  }

  escapeHtml(value) {
    return value
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}
